import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getInitials, cn } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { 
  Phone, PhoneOff, Video, VideoOff, Mic, MicOff, 
  Volume2, VolumeX, ArrowLeft, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import useWebRTC from '../hooks/useWebRTC';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoCallPage() {
  const { callId } = useParams();
  const location = useLocation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [call, setCall] = useState(location.state?.call || null);
  const [loading, setLoading] = useState(!location.state?.call);
  const [callDuration, setCallDuration] = useState(0);
  const [remoteStream, setRemoteStream] = useState(null);
  
  // Media states
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  
  // Media refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const timerRef = useRef(null);

  // Determine if user is caller or callee
  const isCaller = call?.caller_id === user?.id;
  const otherUserId = isCaller ? call?.callee_id : call?.caller_id;

  // WebRTC hook
  const {
    isConnected,
    connectionState,
    startCall,
    answerCall,
    endCall: webrtcEndCall,
    toggleMute: webrtcToggleMute,
    toggleVideo: webrtcToggleVideo,
    cleanup
  } = useWebRTC({
    userId: user?.id,
    callId,
    otherUserId,
    isVideoCall: call?.call_type === 'video',
    isCaller,
    onRemoteStream: (stream) => {
      setRemoteStream(stream);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
    },
    onConnectionStateChange: (state) => {
      console.log('Connection state:', state);
      if (state === 'connected') {
        toast.success('Connected!');
      } else if (state === 'disconnected' || state === 'failed') {
        toast.error('Connection lost');
        handleEndCall();
      }
    },
    onError: (error) => {
      toast.error(error);
    }
  });

  // Fetch call details
  const fetchCall = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calls/${callId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCall(response.data);
    } catch (error) {
      toast.error('Call not found');
      navigate('/chat');
    } finally {
      setLoading(false);
    }
  }, [callId, token, navigate]);

  useEffect(() => {
    if (!call) {
      fetchCall();
    }
  }, [call, fetchCall]);

  // Initialize call based on role
  useEffect(() => {
    const initializeCall = async () => {
      if (!call || !user) return;

      try {
        let stream;
        if (isCaller) {
          // Caller initiates WebRTC
          stream = await startCall();
        } else if (call.status === 'active') {
          // Callee answers WebRTC
          stream = await answerCall();
        }

        if (stream && localVideoRef.current && call.call_type === 'video') {
          localVideoRef.current.srcObject = stream;
        }
      } catch (error) {
        console.error('Failed to initialize call:', error);
      }
    };

    if (call?.status === 'active' || (isCaller && call?.status === 'ringing')) {
      initializeCall();
    }
  }, [call, user, isCaller, startCall, answerCall]);

  // Call duration timer
  useEffect(() => {
    if (call?.status === 'active' && call?.started_at) {
      const startTime = new Date(call.started_at).getTime();
      
      timerRef.current = setInterval(() => {
        const now = Date.now();
        setCallDuration(Math.floor((now - startTime) / 1000));
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [call?.status, call?.started_at]);

  const toggleMute = () => {
    const muted = webrtcToggleMute();
    setIsMuted(muted);
  };

  const toggleVideo = () => {
    const videoOff = webrtcToggleVideo();
    setIsVideoOff(videoOff);
  };

  const handleEndCall = async () => {
    try {
      // End WebRTC connection
      webrtcEndCall();
      
      // Update call status in backend
      await axios.post(`${API}/calls/${callId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      navigate('/chat');
    } catch (error) {
      console.error('Failed to end call:', error);
      cleanup();
      navigate('/chat');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getOtherUser = () => {
    if (!call) return null;
    if (call.caller_id === user?.id) {
      return {
        id: call.callee_id,
        username: call.callee_username,
        avatar: call.callee_avatar,
        display_name: call.callee_display_name
      };
    }
    return {
      id: call.caller_id,
      username: call.caller_username,
      avatar: call.caller_avatar,
      display_name: call.caller_display_name
    };
  };

  const getStatusText = () => {
    if (connectionState === 'connecting' || connectionState === 'new') {
      return 'Connecting...';
    }
    if (connectionState === 'connected' && call?.status === 'active') {
      return formatDuration(callDuration);
    }
    if (call?.status === 'ringing') {
      return isCaller ? 'Calling...' : 'Incoming call...';
    }
    if (call?.status === 'ended') {
      return 'Call ended';
    }
    if (call?.status === 'declined') {
      return 'Call declined';
    }
    return 'Connecting...';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  const otherUser = getOtherUser();
  const isVideoCall = call?.call_type === 'video';

  return (
    <div className="fixed inset-0 bg-black z-50" data-testid="video-call-page">
      {/* Background - Remote Video or Avatar */}
      <div className="absolute inset-0 flex items-center justify-center">
        {isVideoCall && remoteStream ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center">
            <Avatar className="w-32 h-32 mx-auto ring-4 ring-primary/30">
              <AvatarImage src={otherUser?.avatar} />
              <AvatarFallback className="text-4xl bg-primary/20 text-primary">
                {getInitials(otherUser?.display_name || otherUser?.username)}
              </AvatarFallback>
            </Avatar>
            <h2 className="text-2xl font-bold text-white mt-6">
              {otherUser?.display_name || otherUser?.username}
            </h2>
            <p className="text-white/70 mt-2 flex items-center justify-center gap-2">
              {(connectionState === 'connecting' || connectionState === 'new') && (
                <RefreshCw className="w-4 h-4 animate-spin" />
              )}
              {getStatusText()}
            </p>
          </div>
        )}
      </div>

      {/* Local Video (Picture-in-Picture) */}
      {isVideoCall && !isVideoOff && (
        <div className="absolute top-20 right-4 w-32 h-44 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
        </div>
      )}

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent">
        <Button
          variant="ghost"
          size="icon"
          className="text-white rounded-full bg-white/10"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        
        <div className="text-center">
          <p className="text-white text-sm font-medium">
            {isVideoCall ? 'Video Call' : 'Voice Call'}
          </p>
          {connectionState === 'connected' && (
            <p className="text-white/70 text-xs">{formatDuration(callDuration)}</p>
          )}
        </div>
        
        <div className="w-10">
          {/* Connection indicator */}
          <div className={cn(
            "w-3 h-3 rounded-full mx-auto",
            connectionState === 'connected' ? "bg-green-500" :
            connectionState === 'connecting' ? "bg-yellow-500 animate-pulse" :
            "bg-red-500"
          )} />
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-center gap-4">
          {/* Mute Button */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "w-14 h-14 rounded-full",
              isMuted ? "bg-red-500 text-white" : "bg-white/20 text-white"
            )}
            onClick={toggleMute}
            data-testid="mute-btn"
          >
            {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
          </Button>

          {/* Video Toggle (only for video calls) */}
          {isVideoCall && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "w-14 h-14 rounded-full",
                isVideoOff ? "bg-red-500 text-white" : "bg-white/20 text-white"
              )}
              onClick={toggleVideo}
              data-testid="video-toggle-btn"
            >
              {isVideoOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
            </Button>
          )}

          {/* End Call Button */}
          <Button
            variant="destructive"
            size="icon"
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600"
            onClick={handleEndCall}
            data-testid="end-call-btn"
          >
            <PhoneOff className="w-7 h-7" />
          </Button>

          {/* Speaker Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "w-14 h-14 rounded-full",
              !isSpeakerOn ? "bg-red-500 text-white" : "bg-white/20 text-white"
            )}
            onClick={() => setIsSpeakerOn(!isSpeakerOn)}
            data-testid="speaker-btn"
          >
            {isSpeakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Incoming Call Modal Component
export const IncomingCallModal = ({ call, onAnswer, onDecline }) => {
  useEffect(() => {
    // Auto-decline after 30 seconds
    const timeout = setTimeout(() => {
      onDecline();
    }, 30000);

    return () => clearTimeout(timeout);
  }, [onDecline]);

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center" data-testid="incoming-call-modal">
      <div className="text-center">
        <Avatar className="w-28 h-28 mx-auto ring-4 ring-primary animate-pulse">
          <AvatarImage src={call.caller_avatar} />
          <AvatarFallback className="text-3xl bg-primary/20 text-primary">
            {getInitials(call.caller_display_name || call.caller_username)}
          </AvatarFallback>
        </Avatar>
        
        <h2 className="text-2xl font-bold text-white mt-6">
          {call.caller_display_name || call.caller_username}
        </h2>
        
        <p className="text-white/70 mt-2">
          {call.call_type === 'video' ? '📹 Video Call' : '📞 Voice Call'}
        </p>
        
        <p className="text-primary mt-1 animate-pulse">Calling...</p>

        <div className="flex items-center justify-center gap-8 mt-10">
          {/* Decline */}
          <Button
            variant="destructive"
            size="icon"
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600"
            onClick={onDecline}
            data-testid="decline-call-btn"
          >
            <PhoneOff className="w-7 h-7" />
          </Button>

          {/* Answer */}
          <Button
            size="icon"
            className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-600"
            onClick={onAnswer}
            data-testid="answer-call-btn"
          >
            {call.call_type === 'video' ? (
              <Video className="w-7 h-7" />
            ) : (
              <Phone className="w-7 h-7" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
